/**
 * Thin HTTP transport for insight and location APIs.
 * Auth, timeouts, and error handling live here permanently.
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { getApiUrl, getLocationApiUrl } from '../config/api-endpoints.js';
import type { ApiBase } from '@cnapse/shared/api/contract.types.js';
import { STEP_UP_AUTH_HEADER } from '@cnapse/shared/schemas.js';

export const STEP_UP_ERROR_CODES = {
  MFA_REQUIRED: 'STEP_UP_MFA_REQUIRED',
  AUTH_FAILED: 'STEP_UP_AUTH_FAILED',
  TOKEN_REQUIRED: 'STEP_UP_TOKEN_REQUIRED',
  TOKEN_INVALID: 'STEP_UP_TOKEN_INVALID',
} as const;

export const DEFAULT_REQUEST_TIMEOUT_MS = 15_000;

export type ApiRequestOptions = RequestInit & {
  timeout?: number;
  skipAuthRefresh?: boolean;
  base?: ApiBase;
  /** When `blob`, returns `response.blob()` instead of parsing JSON. */
  responseType?: 'json' | 'blob' | 'blob-attachment';
};

export type ApiFormDataUploadOptions = {
  method?: string;
  timeout?: number;
  skipAuthRefresh?: boolean;
  base?: ApiBase;
  onUploadProgress?: (percent: number) => void;
};

export type BlobAttachmentPayload = {
  blob: Blob;
  contentDisposition: string | null;
};

export type TokenPair = {
  accessToken: string;
  refreshToken: string;
};

export class ApiClient {
  private static instance: ApiClient | null = null;
  private accessToken: string | null = null;
  private stepUpToken: string | null = null;

  private constructor() {
    this.accessToken = localStorage.getItem('wafer_access_token');
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  public saveTokensToStorage(tokens: TokenPair): void {
    this.accessToken = tokens.accessToken;
    localStorage.setItem('wafer_access_token', tokens.accessToken);
    localStorage.setItem('wafer_refresh_token', tokens.refreshToken);
  }

  public clearTokensFromStorage(): void {
    this.accessToken = null;
    localStorage.removeItem('wafer_access_token');
    localStorage.removeItem('wafer_refresh_token');
    localStorage.removeItem('wafer_inventory_user');
  }

  public getAccessToken(): string | null {
    return this.accessToken;
  }

  public setStepUpToken(token: string | null): void {
    this.stepUpToken = token;
  }

  public async withStepUpToken<T>(token: string, fn: () => Promise<T>): Promise<T> {
    this.stepUpToken = token;
    try {
      return await fn();
    } finally {
      this.stepUpToken = null;
    }
  }

  public async getFreshAccessToken(): Promise<string | null> {
    const cached = this.accessToken || localStorage.getItem('wafer_access_token');
    if (cached) {
      this.accessToken = cached;
      return cached;
    }

    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      if (accessToken) {
        this.accessToken = accessToken;
        localStorage.setItem('wafer_access_token', accessToken);
        return accessToken;
      }
      return null;
    } catch (error) {
      console.warn('Failed to get fresh token from Amplify, using cached token:', error);
      return this.accessToken || localStorage.getItem('wafer_access_token');
    }
  }

  public getSseAccessToken(): Promise<string | null> {
    return this.getFreshAccessToken();
  }

  public resolveBaseUrl(base: ApiBase = 'insight'): string {
    return base === 'location' ? getLocationApiUrl() : getApiUrl();
  }

  public async request<T>(
    url: string,
    options: ApiRequestOptions = {},
  ): Promise<T> {
    if (!options.skipAuthRefresh) {
      await this.getFreshAccessToken();
    }

    const responseType = options.responseType ?? 'json';

    const headers: Record<string, string> = {
      ...((options.headers as Record<string, string>) || {}),
    };

    // JSON request bodies need Content-Type even when the response is a blob (e.g. bulk download).
    if (
      options.body !== undefined &&
      options.body !== null &&
      !(options.body instanceof FormData)
    ) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    } else {
      console.warn('No access token available for request:', url);
    }

    if (this.stepUpToken) {
      headers[STEP_UP_AUTH_HEADER] = this.stepUpToken;
    }

    const baseUrl = this.resolveBaseUrl(options.base);
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

    const controller = new AbortController();
    const timeoutDuration = options.timeout ?? DEFAULT_REQUEST_TIMEOUT_MS;
    const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

    let data: unknown = null;

    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.status === 401) {
        this.clearTokensFromStorage();
        window.location.href = '/login';
        throw new Error('Session expired, please login again');
      }

      if (!response.ok) {
        let errorData: {
          message?: string;
          error?: string;
          errorCode?: string;
          details?: { existingJobId?: string; phase?: string };
          requiresUpgrade?: boolean;
        } = {};

        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('application/json')) {
          const responseText = await response.text();
          if (responseText.length > 0) {
            errorData = JSON.parse(responseText) as typeof errorData;
          }
        } else if (responseType === 'blob' || responseType === 'blob-attachment') {
          const errorText = await response.text().catch(() => '');
          if (errorText) {
            errorData = { message: errorText };
          }
        }

        if (response.status === 403) {
          const isSubscriptionBlock =
            errorData.requiresUpgrade === true ||
            errorData.error === 'Subscription Required' ||
            errorData.error === 'Subscription Inactive' ||
            errorData.error === 'Subscription Past Due';
          if (isSubscriptionBlock) {
            throw new Error(errorData.message || 'Subscription required');
          }
        }

        const errorMessage =
          errorData.message || errorData.error || `Request failed with status ${response.status}`;
        const error: Error & {
          httpStatus?: number;
          errorCode?: string;
          mfaRequired?: boolean;
          details?: { existingJobId?: string; phase?: string };
        } = new Error(errorMessage);
        error.httpStatus = response.status;
        if (errorData.errorCode) error.errorCode = errorData.errorCode;
        if ('mfaRequired' in errorData && errorData.mfaRequired === true) {
          error.mfaRequired = true;
        }
        if (errorData.details) error.details = errorData.details;
        throw error;
      }

      if (responseType === 'blob') {
        return await response.blob() as T;
      }

      if (responseType === 'blob-attachment') {
        const payload: BlobAttachmentPayload = {
          blob: await response.blob(),
          contentDisposition: response.headers.get('Content-Disposition'),
        };
        return payload as T;
      }

      const responseText = await response.text();
      data = responseText.length > 0 ? JSON.parse(responseText) : null;

      if (
        data &&
        typeof data === 'object' &&
        'error' in data &&
        'success' in data &&
        (data as { success: boolean }).success === false
      ) {
        const errorMessage = (data as { error?: string }).error;
        if (
          typeof errorMessage === 'string' &&
          (errorMessage.includes('User not found') ||
            errorMessage.includes('user not found') ||
            errorMessage.includes('User account is inactive') ||
            errorMessage.includes('Tenant not found'))
        ) {
          this.clearTokensFromStorage();
          window.location.href = '/login';
          throw new Error('Authentication failed, please login again');
        }
      }

      return data as T;
    } catch (error) {
      clearTimeout(timeoutId);

      const httpStatus = (error as { httpStatus?: number }).httpStatus;
      if (httpStatus !== 404) {
        console.error('ApiClient.request error:', error);
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      if (error instanceof Error) throw error;
      throw new Error('Network error');
    }
  }

  /**
   * Multipart upload with XMLHttpRequest progress events (fetch cannot report upload progress).
   */
  public async uploadFormDataWithProgress<T>(
    url: string,
    formData: FormData,
    options: ApiFormDataUploadOptions = {},
  ): Promise<T> {
    if (!options.skipAuthRefresh) {
      await this.getFreshAccessToken();
    }

    const baseUrl = this.resolveBaseUrl(options.base);
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
    const timeoutDuration = options.timeout ?? DEFAULT_REQUEST_TIMEOUT_MS;
    const method = options.method ?? 'POST';

    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const timeoutId = window.setTimeout(() => {
        xhr.abort();
        reject(new Error('Request timeout - please try again'));
      }, timeoutDuration);

      xhr.open(method, fullUrl);
      if (this.accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.accessToken}`);
      } else {
        console.warn('No access token available for upload:', url);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && options.onUploadProgress) {
          options.onUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        window.clearTimeout(timeoutId);

        if (xhr.status === 401) {
          this.clearTokensFromStorage();
          window.location.href = '/login';
          reject(new Error('Session expired, please login again'));
          return;
        }

        let result: unknown;
        try {
          result = xhr.responseText.length > 0 ? JSON.parse(xhr.responseText) : null;
        } catch {
          reject(new Error('Invalid server response'));
          return;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(result as T);
          return;
        }

        const errorData = result as { message?: string; error?: string };
        reject(new Error(errorData.message || errorData.error || `Request failed with status ${xhr.status}`));
      };

      xhr.onerror = () => {
        window.clearTimeout(timeoutId);
        reject(new Error('Network error during upload'));
      };

      xhr.onabort = () => {
        window.clearTimeout(timeoutId);
        reject(new Error('Upload cancelled'));
      };

      xhr.send(formData);
    });
  }
}

export const apiClient = ApiClient.getInstance();
